/*
 * saunt-lazy-image-fix.js
 *
 * Clears stuck loading state on product card images in Be Yours 8.1.0.
 *
 * BACKGROUND
 *
 * Card images render as <img is="lazy-image">, defined in lazyimage.js.
 * On construction it adds a 'loading' class to the enclosing
 * .media-wrapper and removes it again when the image fires its load
 * event. Two things break that contract:
 *
 *   1. If the image completes BEFORE the custom element upgrades and
 *      attaches its listener — cached images, and eager above-the-fold
 *      cards — the load event never fires. The wrapper keeps 'loading'
 *      forever: the image sits at opacity 0 and the loading bar runs
 *      indefinitely.
 *
 *   2. Slick clones every slide to build its infinite loop. Each clone
 *      constructs a fresh lazy-image, which re-adds 'loading' to the
 *      shared .card__media wrapper after the page has settled.
 *
 * Cause 2 is why the loading bar appeared on mobile collection cards
 * once the slider began initialising there.
 *
 * The earlier version of this file only handled cause 1, and it swept
 * DOWNWARD — on a DOM insertion it searched for .card__media.loading
 * *inside* the inserted node. Slick's clones are descendants of
 * .card__media, never containers of it, so those sweeps found nothing
 * and the re-armed loading class was never cleared.
 *
 * WHAT THIS DOES
 *
 *   - sweeps the whole document rather than only the inserted subtree,
 *     debounced so a burst of Slick insertions costs one pass
 *   - also walks UP from an inserted node to its wrapper ancestor
 *   - re-sweeps on resize and orientationchange, because lazyimage.js
 *     re-runs handleLazy() on resize and can re-add 'loading'
 *   - runs a few delayed sweeps after load to catch Slick finishing
 *     asynchronously
 *
 * The observer watches childList only, so removing a class cannot
 * retrigger it — there is no feedback loop.
 */
(function () {
  'use strict';

  var WRAPPER = '.card__media.loading, .media-wrapper.loading';

  function settle(wrapper) {
    var imgs = wrapper.querySelectorAll('img');
    if (!imgs.length) {
      wrapper.classList.remove('loading');
      return;
    }

    var anyComplete = false;

    Array.prototype.forEach.call(imgs, function (img) {
      if (img.complete && img.naturalWidth > 0) {
        anyComplete = true;
        return;
      }
      var clear = function () {
        wrapper.classList.remove('loading');
      };
      img.addEventListener('load', clear, { once: true });
      img.addEventListener('error', clear, { once: true });
    });

    if (anyComplete) wrapper.classList.remove('loading');
  }

  function sweep() {
    var found = document.querySelectorAll(WRAPPER);
    Array.prototype.forEach.call(found, settle);
  }

  var timer = null;
  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(sweep, delay || 100);
  }

  sweep();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sweep);
  }

  window.addEventListener('load', function () {
    sweep();
    // Slick initialises after load and inserts its clones asynchronously.
    setTimeout(sweep, 400);
    setTimeout(sweep, 1200);
    setTimeout(sweep, 2500);
  });

  window.addEventListener('resize', function () { schedule(250); });
  window.addEventListener('orientationchange', function () { schedule(250); });

  if ('MutationObserver' in window) {
    new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType !== 1) continue;

          // Walk up as well as down: a Slick clone sits inside the
          // wrapper whose class needs clearing.
          if (added[j].closest) {
            var up = added[j].closest('.card__media, .media-wrapper');
            if (up && up.classList.contains('loading')) settle(up);
          }

          schedule(120);
          return;
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
